/**
 * i18n configuration for EquiSight.
 *
 * Strategy:
 *   - 26 languages total (NL = default, no URL prefix; 25 others use /{lang}/ subpath).
 *   - Browser language is auto-detected on first visit.
 *   - User choice is persisted in localStorage.
 *   - UI strings live in /app/frontend/src/i18n/locales/{lang}.json
 *   - Blog content is translated separately into /app/frontend/src/data/blog.{lang}.js
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// All 26 supported locales — { code, label (native), flag (emoji fallback), country (ISO 3166-1 alpha-2 for flag-icons), englishName }
export const LOCALES = [
  { code: "nl", country: "nl", label: "Nederlands", flag: "🇳🇱", englishName: "Dutch" },
  { code: "en", country: "gb", label: "English", flag: "🇬🇧", englishName: "English" },
  { code: "de", country: "de", label: "Deutsch", flag: "🇩🇪", englishName: "German" },
  { code: "fr", country: "fr", label: "Français", flag: "🇫🇷", englishName: "French" },
  { code: "es", country: "es", label: "Español", flag: "🇪🇸", englishName: "Spanish" },
  { code: "it", country: "it", label: "Italiano", flag: "🇮🇹", englishName: "Italian" },
  { code: "pt", country: "pt", label: "Português", flag: "🇵🇹", englishName: "Portuguese" },
  { code: "pl", country: "pl", label: "Polski", flag: "🇵🇱", englishName: "Polish" },
  { code: "sv", country: "se", label: "Svenska", flag: "🇸🇪", englishName: "Swedish" },
  { code: "da", country: "dk", label: "Dansk", flag: "🇩🇰", englishName: "Danish" },
  { code: "fi", country: "fi", label: "Suomi", flag: "🇫🇮", englishName: "Finnish" },
  { code: "no", country: "no", label: "Norsk", flag: "🇳🇴", englishName: "Norwegian" },
  { code: "cs", country: "cz", label: "Čeština", flag: "🇨🇿", englishName: "Czech" },
  { code: "sk", country: "sk", label: "Slovenčina", flag: "🇸🇰", englishName: "Slovak" },
  { code: "hu", country: "hu", label: "Magyar", flag: "🇭🇺", englishName: "Hungarian" },
  { code: "ro", country: "ro", label: "Română", flag: "🇷🇴", englishName: "Romanian" },
  { code: "bg", country: "bg", label: "Български", flag: "🇧🇬", englishName: "Bulgarian" },
  { code: "el", country: "gr", label: "Ελληνικά", flag: "🇬🇷", englishName: "Greek" },
  { code: "et", country: "ee", label: "Eesti", flag: "🇪🇪", englishName: "Estonian" },
  { code: "lv", country: "lv", label: "Latviešu", flag: "🇱🇻", englishName: "Latvian" },
  { code: "lt", country: "lt", label: "Lietuvių", flag: "🇱🇹", englishName: "Lithuanian" },
  { code: "hr", country: "hr", label: "Hrvatski", flag: "🇭🇷", englishName: "Croatian" },
  { code: "sl", country: "si", label: "Slovenščina", flag: "🇸🇮", englishName: "Slovenian" },
  { code: "ga", country: "ie", label: "Gaeilge", flag: "🇮🇪", englishName: "Irish" },
  { code: "mt", country: "mt", label: "Malti", flag: "🇲🇹", englishName: "Maltese" },
  { code: "zh", country: "cn", label: "中文", flag: "🇨🇳", englishName: "Chinese (Simplified)" },
  { code: "ar", country: "sa", label: "العربية", flag: "🇸🇦", englishName: "Arabic", rtl: true },
];

// Right-to-left locales
export const RTL_LOCALES = LOCALES.filter((l) => l.rtl).map((l) => l.code);

export const DEFAULT_LOCALE = "nl";
export const LOCALE_CODES = LOCALES.map((l) => l.code);

// Lazy-load all locale JSONs via require so bundler resolves at build time.
const resources = LOCALES.reduce((acc, l) => {
  try {
    // eslint-disable-next-line global-require
    acc[l.code] = { translation: require(`./locales/${l.code}.json`) };
  } catch (e) {
    // Locale file not yet generated — fall back silently.
  }
  return acc;
}, {});

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: LOCALE_CODES,
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      // Priority: URL path → localStorage → navigator → default.
      order: ["path", "localStorage", "navigator", "htmlTag"],
      lookupFromPathIndex: 0,
      caches: ["localStorage"],
      lookupLocalStorage: "equisight_lng",
    },
    react: { useSuspense: false },
  });

export default i18n;

/** Strip a leading /{lang} from a pathname (for the current location). */
export function stripLocaleFromPath(pathname) {
  const m = pathname.match(/^\/([a-z]{2})(\/|$)/);
  if (m && LOCALE_CODES.includes(m[1]) && m[1] !== DEFAULT_LOCALE) {
    return pathname.slice(3) || "/";
  }
  return pathname;
}

/** Detect the locale from a pathname (returns "nl" for unprefixed paths). */
export function localeFromPath(pathname) {
  const m = pathname.match(/^\/([a-z]{2})(\/|$)/);
  if (m && LOCALE_CODES.includes(m[1]) && m[1] !== DEFAULT_LOCALE) {
    return m[1];
  }
  return DEFAULT_LOCALE;
}

/** Build a localized href for a given path + locale. */
export function localizedPath(path, locale) {
  const clean = stripLocaleFromPath(path);
  if (!locale || locale === DEFAULT_LOCALE) return clean;
  if (clean === "/") return `/${locale}`;
  return `/${locale}${clean}`;
}
